import { api } from './api';

export type SubscriptionCheckoutPlan = {
  id: number;
  type: 'Basic' | 'Premium';
  description: string;
  price_in_cents: number;
  benefits: string[];
};

export type SubscriptionCheckoutResponse = {
  checkout_url: string;
  transaction_id: string;
  plan: SubscriptionCheckoutPlan;
};

export const customizeOptions = api.injectEndpoints({
  endpoints: builder => ({
    getActivityInterest: builder.query({
      query: params => {
        return {
          url: `${process.env.REACT_APP_API_URL}/users/profile/customize/options/?option=${params}`,
        };
      },
      keepUnusedDataFor: 5,
    }),
    getUser: builder.query({
      query: () => {
        return {
          url: `${process.env.REACT_APP_API_URL}/users/me/`,
        };
      },
      providesTags: ['User'],
    }),
    updateUserAdditionalInfo: builder.mutation({
      query: payload => {
        return {
          url: `${process.env.REACT_APP_API_URL}/users/profile/customize/`,
          method: 'PATCH',
          body: {
            nearest_city: payload.nearest_city,
            nearest_airport: payload.nearest_airport,
            unit_system: payload.unit_system,
            travel_preference: payload.travel_preference,
            is_inviting_others: payload.is_inviting_others,
            budget_preference: payload.budget_preference,
            pace_preference: payload.pace_preference,
            primary_travel_purposes: payload.primary_travel_purposes,
            destination_types: payload.destination_types,
            activity_interests: payload.activity_interests,
            trip_planning_style: payload.trip_planning_style,
          },
        };
      },
      invalidatesTags: ['User'],
    }),
    updateUserInfo: builder.mutation({
      query: ({
        id,
        first_name,
        last_name,
        email,
        username,
        gender,
        age_group,
        country,
        nearest_city_and_airport,
        chosen_plan,
        primary_travel_purposes,
        destination_types,
        activity_interests,
        travel_preference,
        budget_preference,
        pace_preference,
      }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/users/me/`,
          method: 'PATCH',
          body: {
            id,
            first_name,
            last_name,
            email,
            username,
            gender,
            age_group,
            country,
            nearest_city_and_airport,
            chosen_plan,
            primary_travel_purposes,
            destination_types,
            activity_interests,
            travel_preference,
            budget_preference,
            pace_preference,
          },
        };
      },
      invalidatesTags: ['User'],
    }),
    updatePlan: builder.mutation({
      query: ({ plan_id }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/users/me/plan/`,
          method: 'PUT',
          body: {
            plan_id: plan_id,
          },
        };
      },
      invalidatesTags: ['User'],
    }),
    cancelSubscription: builder.mutation<void, void>({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/payments/subscription/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    subscriptionCheckout: builder.mutation<SubscriptionCheckoutResponse, { plan_id: number }>({
      query: ({ plan_id }) => ({
        url: `${process.env.REACT_APP_API_URL}/payments/subscription/checkout/`,
        method: 'POST',
        body: { plan_id },
      }),
    }),
    getUserSettings: builder.query<
      {
        show_notifications: boolean;
        two_factor_enabled: boolean;
        show_trip_budgets: boolean;
        currency: string;
        unit_system: string;
      },
      void
    >({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/users/me/settings/`,
      }),
      keepUnusedDataFor: 5,
      providesTags: ['User'],
    }),
    updateUserSettings: builder.mutation<
      {
        show_notifications: boolean;
        two_factor_enabled: boolean;
        show_trip_budgets: boolean;
        currency: string;
        unit_system: string;
      },
      Partial<{
        show_notifications: boolean;
        two_factor_enabled: boolean;
        show_trip_budgets: boolean;
        currency: string;
        unit_system: string;
      }>
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_API_URL}/users/me/settings/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useGetActivityInterestQuery,
  useGetUserQuery,
  useUpdateUserInfoMutation,
  useUpdateUserAdditionalInfoMutation,
  useUpdatePlanMutation,
  useCancelSubscriptionMutation,
  useSubscriptionCheckoutMutation,
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} = customizeOptions;
